import { useEffect, useState } from 'react';

import modalStatusService, { type PropsModalStatus } from '~/services/modalStatus';
import { cn } from '~/utils/cn';

import Button from '../../atoms/Button/view';
import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';
import Backdrop from '../../molecules/Backdrop/view';

type State = (PropsModalStatus & { visible: true }) | { visible: false };

const TYPE_STYLE: Record<NonNullable<PropsModalStatus['type']>, { bg: string; bd: string; color: string }> = {
  icon: { bg: 'bg-support-01', bd: 'border-support-01', color: '#1D4733' },
  success: { bg: 'bg-success-01', bd: 'border-success-01', color: '#007831' },
  error: { bg: 'bg-warning-01', bd: 'border-warning-01', color: '#992929' },
  alert: { bg: 'bg-alert-01', bd: 'border-alert-01', color: '#99701F' },
};

export default function ModalStatus() {
  const [state, setState] = useState<State>({ visible: false });

  useEffect(() => {
    const unsubscribe = modalStatusService.subscribe(setState);
    return () => {
      unsubscribe();
    };
  }, []);

  if (!state.visible) return null;

  const variant = state.type ?? 'icon';
  const style = TYPE_STYLE[variant];

  return (
    <Backdrop open onClose={state.handleCloseModal ?? modalStatusService.hide} position="center">
      <div className="p-medium flex flex-col items-center text-center">
        <div
          className={cn(
            'h-[80px] w-[80px] rounded-full flex items-center justify-center border-2',
            state.iconBgColor ?? style.bg,
            state.iconBorderColor ?? style.bd,
          )}
        >
          <Icon
            name={state.icon ?? 'icon-circle-alert'}
            size={36}
            color={state.iconColor ?? style.color}
            rounded={false}
          />
        </div>
        <Text className="mt-medium text-headingH1 color-complement-01">
          {state.title}
        </Text>
        {state.description ? (
          <Text className="mt-quark text-bodyMD color-intense-06">
            {state.description}
          </Text>
        ) : null}
        <div className="mt-medium w-full flex flex-col gap-nano">
          {state.showFirstButton && state.textFirstButton ? (
            <Button
              text={state.textFirstButton}
              onClick={state.onClickFirstButton}
              fullWidth
            />
          ) : null}
          {state.showSecondButton && state.textSecondButton ? (
            <Button
              text={state.textSecondButton}
              variant="ghost"
              onClick={state.onClickSecondButton}
              fullWidth
            />
          ) : null}
        </div>
      </div>
    </Backdrop>
  );
}
